import { useNavigate } from "react-router-dom-v5-compat";
import { getCarRoutes } from "@b2bportal/core-cars";
import { useModuleBEM } from "@b2bportal/core-themes";
import { FLIGHTS_PATH } from "@b2bportal/core-utilities";
import {
  DiscountEnum,
  EligibleProduct,
  type WalletOffer,
} from "@b2bportal/wallet-offer-api";
import { getPercentage, useI18nContext } from "@hopper-b2b/i18n";
import { Button, ButtonTypeEnum } from "@tenant/ui";

import styles from "./WalletOffersSection.module.scss";

import { HOTELS_PATH } from "@/utilities";

export interface WalletOfferCardProps {
  offer: WalletOffer;
  product: EligibleProduct;
  /**
   * Card heading. The design shows a destination here, but the WalletOffer
   * contract has no destination field, so the section passes a hardcoded
   * placeholder for now. TODO(rbc-travel-wallet): source from the offer once
   * the BE adds a destination field.
   */
  destination: string;
}

const useProductPath = (product: EligibleProduct): string => {
  const { BASE_ROUTE: CARS_PATH } = getCarRoutes();

  switch (product) {
    case EligibleProduct.Air:
      return FLIGHTS_PATH;
    case EligibleProduct.Ground:
      return CARS_PATH;
    case EligibleProduct.Home:
    case EligibleProduct.Lodging:
    default:
      return HOTELS_PATH;
  }
};

/**
 * - TODO(rbc-travel-wallet): the contract has no deep link for the CTA yet —
 *   until the BE provides one (e.g. a destination hotel search link), "Claim
 *   now" navigates to the product funnel's landing page.
 */
export const WalletOfferCard = ({
  offer,
  product,
  destination,
}: WalletOfferCardProps) => {
  const { t, locale, formatFiatCurrency } = useI18nContext();
  const [block, cn] = useModuleBEM(styles, "WalletOfferCard");
  const navigate = useNavigate();
  const productPath = useProductPath(product);

  const isLodging =
    product === EligibleProduct.Lodging || product === EligibleProduct.Home;

  let tagLabel: string;
  if (offer.discount.Discount === DiscountEnum.Percentage) {
    const percent = getPercentage(offer.discount.percentage * 100, locale);
    tagLabel = isLodging
      ? t("walletOffersSection.percentOffHotels", { percent })
      : t("walletOffersSection.percentOff", { percent });
  } else {
    const amount = formatFiatCurrency(offer.discount.amount.fiat);
    tagLabel = isLodging
      ? t("walletOffersSection.amountOffHotels", { amount })
      : t("walletOffersSection.amountOff", { amount });
  }

  const imageUrl = offer.offerContent.imageUrl;
  const hasSafeImage = imageUrl?.startsWith("https://") ?? false;

  return (
    <div className={block}>
      <div className={cn("image-container", { fallback: !hasSafeImage })}>
        {hasSafeImage ? (
          <img className={cn("image")} src={imageUrl} alt={destination} />
        ) : null}
        <span className={cn("tag")}>{tagLabel}</span>
      </div>
      <div className={cn("body")}>
        <h3 className={cn("title")}>{destination}</h3>
        <p className={cn("description")}>
          {offer.offerContent.lineItemDescription}
        </p>
        <Button
          buttonType={ButtonTypeEnum.Primary}
          className={cn("cta")}
          onClick={() => navigate(productPath)}
        >
          {t("walletOffersSection.cta")}
        </Button>
      </div>
    </div>
  );
};
